import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useCatalog from '../lib/useCatalog';
import Art from './Art';
import PosterCard, { useOpenTitle } from './PosterCard';
import CatalogNotice from './CatalogNotice';
import '../person.css';

// Biographies run to essay length on anyone famous; past this the page is
// about the person instead of their work, so the rest sits behind "more".
const BIO_CLAMP = 420;
const BIO_CLAMP_PHONE = 160;
const PHONE = '(max-width: 760px)';

// The main grid shows this many before "show all": a busy actor has well over
// a hundred credits, and the bottom of that list is uncredited walk-ons.
const GRID_PAGE = 30;
const OTHER_PAGE = 8;

// A role becomes a tab of its own once there is enough of it to browse;
// anything smaller goes to "Other credits" with the crew departments.
const TAB_ROLES = ['directing', 'acting', 'writing', 'producing'];
const TAB_MIN = 3;

// Below this many votes a score is a handful of people, and a 10.0 short
// would otherwise head every "top rated" list.
const RATING_MIN_VOTES = 25;

const SORTS = [
  { key: 'popular', label: 'Popular' },
  { key: 'newest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'rating', label: 'Rating', hint: 'TMDB user score' }
];

const KINDS = [
  { key: 'all', label: 'All' },
  { key: 'movie', label: 'Films' },
  { key: 'series', label: 'Series' }
];

// Undated credits are announced or in development; they sit at the end of
// either date order rather than pretending to a year.
const byDate = (direction) => (a, b) => {
  if (!a.date !== !b.date) return a.date ? -1 : 1;
  return direction * String(a.date || '').localeCompare(String(b.date || ''));
};

const SORTERS = {
  popular: (a, b) => (b.votes || 0) - (a.votes || 0) || (b.popularity || 0) - (a.popularity || 0),
  newest: byDate(-1),
  oldest: byDate(1),
  rating: (a, b) => {
    const ra = (a.votes || 0) >= RATING_MIN_VOTES;
    const rb = (b.votes || 0) >= RATING_MIN_VOTES;
    if (ra !== rb) return ra ? -1 : 1;
    return (b.rating || 0) - (a.rating || 0) || (b.votes || 0) - (a.votes || 0);
  }
};

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "Cobb" / "Executive Producer" / "12 episodes" — whatever says what they did. */
const roleLine = (credit, roleKey) => {
  const parts = [];
  // Directed needs no job line: the tab already says it
  if (credit.role && roleKey !== 'directing') parts.push(credit.role);
  if (credit.episodes) parts.push(plural(credit.episodes, 'ep'));
  return parts.join(' · ');
};

const CreditCaption = ({ credit, roleKey }) => {
  const role = roleLine(credit, roleKey);
  return (
    <>
      <span className="pf-name">{credit.name}</span>
      <span className="pf-meta">
        {credit.year || 'TBA'}
        {credit.rating ? <span className="pf-star">★ {credit.rating}</span> : null}
        {credit.type === 'series' ? <span className="pf-kind">TV</span> : null}
      </span>
      {role && <span className="pf-role">{role}</span>}
    </>
  );
};

/** The compact rows for one minor role, under "Other credits". */
const OtherGroup = ({ group }) => {
  const openTitle = useOpenTitle();
  const [all, setAll] = useState(false);
  const rows = useMemo(() => [...group.titles].sort(SORTERS.popular), [group]);
  const shown = all ? rows : rows.slice(0, OTHER_PAGE);

  return (
    <div className="pf-other-group">
      <div className="pf-other-head">
        <span>{group.label}</span>
        <span className="pf-other-count">{group.count}</span>
      </div>
      <ul className="pf-other-list">
        {shown.map((credit) => (
          <li key={`${credit.type}-${credit.id}`}>
            <a
              href={`/title/${credit.type}/${credit.id}`}
              className="pf-other-row"
              onClick={(event) => {
                event.preventDefault();
                openTitle(credit);
              }}
            >
              <span className="pf-other-year">{credit.year || '—'}</span>
              <span className="pf-other-title">{credit.name}</span>
              <span className="pf-other-role">{roleLine(credit, group.key)}</span>
            </a>
          </li>
        ))}
      </ul>
      {rows.length > OTHER_PAGE && (
        <button type="button" className="pf-other-more" onClick={() => setAll(!all)}>
          {all ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
};

/**
 * A person's page: who they are, then the work they are known for.
 *
 * The lead section is their primary role - a director's films, an actor's
 * performances - sortable by popularity, date or score and filterable to
 * films or series. Anyone with more than one real body of work gets a tab per
 * role, opening on the primary. Cameos as themselves, crew odds and ends and
 * roles too small for a tab sit folded away under "Other credits", where they
 * cannot push the main list down the page.
 *
 * Role, sort and kind live in the query string, so the back button and a
 * shared link both land on the same view.
 */
const Person = () => {
  const { id } = useParams();
  const { data: person, loading, error, notConfigured } = useCatalog(`/api/catalog/person/${id}`);
  const [params, setParams] = useSearchParams();
  const [bioOpen, setBioOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const roles = useMemo(() => person?.roles || [], [person]);
  const tabs = useMemo(
    () => roles.filter((group) => group.key === person?.primary
      || (TAB_ROLES.includes(group.key) && group.count >= TAB_MIN)),
    [roles, person]
  );
  const others = roles.filter((group) => !tabs.includes(group));

  const active = tabs.find((group) => group.key === params.get('role')) || tabs[0] || null;
  const sort = SORTERS[params.get('sort')] ? params.get('sort') : 'popular';
  const hasBoth = active ? active.films > 0 && active.series > 0 : false;
  const kind = hasBoth && ['movie', 'series'].includes(params.get('type')) ? params.get('type') : 'all';

  const credits = useMemo(() => {
    if (!active) return [];
    const rows = kind === 'all' ? active.titles : active.titles.filter((title) => title.type === kind);
    return [...rows].sort(SORTERS[sort]);
  }, [active, kind, sort]);

  const setParam = (key, value, fallback) => {
    const next = new URLSearchParams(params);
    if (value === fallback) next.delete(key);
    else next.set(key, value);
    // The kind filter belongs to one role; a director's "Series" choice means
    // nothing on their writing tab
    if (key === 'role') next.delete('type');
    setExpanded(false);
    setParams(next, { replace: true, state: window.history.state?.usr });
  };

  if (loading) return <div className="page loading">LOADING PERSON</div>;
  if (notConfigured) {
    return (
      <div className="page">
        <CatalogNotice />
      </div>
    );
  }
  if (error) return <div className="page alert">{error}</div>;

  const clamp = window.matchMedia?.(PHONE).matches ? BIO_CLAMP_PHONE : BIO_CLAMP;
  const longBio = (person.biography || '').length > clamp;
  const bio = bioOpen || !longBio
    ? person.biography
    : `${person.biography.slice(0, clamp).replace(/\s+\S*$/, '')}…`;

  const lived = [person.born, person.died && `died ${person.died}`].filter(Boolean).join(' · ');
  const lead = tabs.find((group) => group.key === person.primary);
  const shown = expanded ? credits : credits.slice(0, GRID_PAGE);
  const otherCount = others.reduce((sum, group) => sum + group.count, 0);

  return (
    <div className="page pf">
      <div className="person-head">
        <Art src={person.photo} alt={person.name} label="HEADSHOT" className="art--poster person-photo" />
        <div className="person-body">
          <div className="kicker">
            {(lead?.label || person.knownFor || 'FILMOGRAPHY').toUpperCase()}
            {lived && (
              <>
                <span className="kicker-rule" />
                <span className="kicker-alt">{lived.toUpperCase()}</span>
              </>
            )}
          </div>
          <h1 className="page-title person-title">{person.name}</h1>
          {person.from && <p className="page-sub">{person.from}</p>}
          {bio && (
            <p className="person-bio">
              {bio}
              {longBio && (
                <button type="button" className="person-more" onClick={() => setBioOpen(!bioOpen)}>
                  {bioOpen ? 'less' : 'more'}
                </button>
              )}
            </p>
          )}
        </div>
      </div>

      {active && (
        <section className="pf-main">
          <div className="pf-bar">
            {tabs.length > 1 ? (
              <div className="seg pf-roles" role="tablist" aria-label="Role">
                {tabs.map((group) => (
                  <button
                    key={group.key}
                    type="button"
                    role="tab"
                    aria-selected={group === active}
                    className={`pf-seg${group === active ? ' is-active' : ''}`}
                    onClick={() => setParam('role', group.key, tabs[0].key)}
                  >
                    {group.label}
                    <span className="pf-seg-count">{group.count}</span>
                  </button>
                ))}
              </div>
            ) : (
              <h2 className="pf-heading">
                {active.label}
                <span className="pf-seg-count">{active.count}</span>
              </h2>
            )}

            <div className="pf-controls">
              {hasBoth && (
                <div className="seg pf-kinds" aria-label="Show">
                  {KINDS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={`pf-seg${kind === option.key ? ' is-active' : ''}`}
                      onClick={() => setParam('type', option.key, 'all')}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="seg pf-sorts" aria-label="Sort by">
                {SORTS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    title={option.hint}
                    className={`pf-seg${sort === option.key ? ' is-active' : ''}`}
                    onClick={() => setParam('sort', option.key, 'popular')}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="pf-grid">
            {shown.map((credit) => (
              <PosterCard
                key={`${credit.type}-${credit.id}`}
                title={credit}
                variant="grid"
                shape="poster"
                caption={<CreditCaption credit={credit} roleKey={active.key} />}
              />
            ))}
          </div>

          {credits.length > GRID_PAGE && (
            <button type="button" className="btn btn--soft pf-all" onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Show fewer' : `Show all ${credits.length}`}
            </button>
          )}
        </section>
      )}

      {others.length > 0 && (
        <details className="pf-other">
          <summary>
            <span className="pf-other-title-main">Other credits</span>
            <span className="pf-other-sum">
              {others.map((group) => group.label).join(' · ')} · {plural(otherCount, 'title')}
            </span>
            <span className="chev pf-other-chev" />
          </summary>
          <div className="pf-other-body">
            {others.map((group) => (
              <OtherGroup key={group.key} group={group} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default Person;
